import { useEffect, useRef, useState } from 'react';

export default function About() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="about"
      ref={sectionRef}
      className="relative min-h-screen flex items-center py-20 px-6 md:px-20"
    >
      <div className="max-w-5xl mx-auto">
        <div
          className={`transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-1 bg-white rounded-full" />
            <span className="text-xs tracking-[0.2rem] text-white/50 uppercase">
              About
            </span>
          </div>

          <h2
            className="text-5xl md:text-6xl font-black text-white mb-12 tracking-tight"
            style={{ fontFamily: 'Impact, "Arial Black", sans-serif', fontWeight: 900 }}
          >
            HigherEXcellence
          </h2>

          <div className="text-lg leading-relaxed text-white/70 space-y-8" style={{ fontWeight: 300 }}>
            <p>
              HigherEX aka HEX. A veteran of the craft and a sonic architect of the underground. I build fresh immersive soundscapes using a surgical blend of analog warmth and digital precision. My work exists in the space between the light and the dark. Transcending conventional genre styles to deliver an experience that isn't just heard, but felt. Producer. DJ. MC. Architect.
            </p>

            <p>
              With years of mastery behind the boards and on the mic, I have evolved into a sonic architect. By merging the grit of analog mediums with the limitless potential of digital soundscapes, I create music that transcends conventional boundaries. Whether I'm behind the decks, in the booth, or at the console, my mission remains the same: to craft immersive environments that challenge the listener's perception.
            </p>

            <div className="pt-6">
              <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">
                PHILOSOPHY
              </h3>
              <p>
                I build at the intersection of frequency and flow. Music is more than geometry; it is a vibrational signature that speaks to the core of our being. By mastering the resonance of both the light and the dark, I construct immersive soundscapes that recalibrate the human frequency, turning every track into a deliberate act of rhythmic alignment.
              </p>
            </div>

            <div className="pt-6">
              <h3 className="text-2xl font-bold text-white mb-4 tracking-tight">
                APPROACH
              </h3>
              <p>
                <strong className="text-white">On the Mic:</strong> I'm a high-frequency conductor, dropping bars that bridge the gap between raw mathematics and human soul. <strong className="text-white">On the Decks:</strong> I'm a rhythmic specialist, manipulating the physical rotation of sound to find the perfect harmonic resonance. <strong className="text-white">In the Lab:</strong> I'm a digital-analog hybrid, constructing immersive soundscapes from the dust of the past and the frequencies of the future.
              </p>
              <p className="mt-6 font-bold text-white text-center">
                Hip-Hop Music - Hip Hop Culture - HipHop Spirit
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
